import { useState, useRef } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  canSeeReportCard,
  getNavPermissionContext,
} from 'src/Common/permissions/cloudPosPermissions';
import { useSettingsTokens } from 'src/content/applications/Settings/CompanySettings/settingsUi';

/**
 * Classic (new-look) header dropdowns — the Claude / ChatGPT menu: a flat surface with a
 * hairline border, 12px corners and quiet 36px rows (14px text, grey icons, soft grey hover).
 * Returns null under the company "Full Modern" look, whose menus stay exactly as they are.
 */
export function useChromeMenuSx() {
  const theme = useTheme();
  const k = useSettingsTokens();
  if ((theme as any).isModern) return null;
  const dark = theme.palette.mode === 'dark';
  return {
    paper: {
      p: '4px',
      bgcolor: dark ? k.navBg : k.pageBg,
      backgroundImage: 'none',
      border: `1px solid ${k.border}`,
      borderRadius: '12px',
      boxShadow: dark ? '0 12px 32px rgba(0,0,0,0.45)' : '0 12px 32px rgba(0,0,0,0.10)'
    },
    list: {
      p: 0,
      '& .MuiMenuItem-root.MuiButtonBase-root': {
        minHeight: 36,
        px: '10px',
        py: '6px',
        my: '1px',
        borderRadius: '8px',
        fontSize: 14,
        fontWeight: 400,
        color: k.text,
        '& .MuiListItemIcon-root': { minWidth: 30, color: k.navText },
        '& .MuiListItemIcon-root .MuiSvgIcon-root': { fontSize: 18 },
        '& .MuiListItemText-primary': { fontSize: 14 },
        '&:hover, &:active, &.active, &.Mui-selected': { color: k.text, background: k.hover },
        '&.active': { fontWeight: 500 }
      },
      '& .MuiDivider-root': { bgcolor: k.border }
    },
    k
  };
}

const ListWrapper = styled(Box)(
  ({ theme }) => `
        .MuiTouchRipple-root {
            display: none;
        }
        
        .MuiListItem-root {
            transition: ${theme.transitions.create(['color', 'fill'])};
            
            &.MuiListItem-indicators {
                padding: ${theme.spacing(1, 2)};
            
                .MuiListItemText-root {
                    .MuiTypography-root {
                        &:before {
                            height: 4px;
                            width: 22px;
                            opacity: 0;
                            visibility: hidden;
                            display: block;
                            position: absolute;
                            bottom: -10px;
                            transition: all .2s;
                            border-radius: ${theme.general.borderRadiusLg};
                            content: "";
                            background: ${theme.colors.primary.main};
                        }
                    }
                }

                &.active,
                &:active,
                &:hover {
                
                    background: transparent;
                
                    .MuiListItemText-root {
                        .MuiTypography-root {
                            &:before {
                                opacity: 1;
                                visibility: visible;
                                bottom: 0px;
                            }
                        }
                    }
                }
            }
        }
`
);

function HeaderMenu() {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  // Read on the first render (not in an effect) so the menu never shows entries this user may not see.
  const [roles] = useState<string[]>(() => {
    const storedRoles = localStorage.getItem('userRoles');
    return storedRoles ? storedRoles.split(',').map((role) => role.trim()).filter(Boolean) : [];
  });

  const forms = [
    { name: "1. Balance Sheet", path: "/reports/balancesheet/", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
    { name: "2. Profit Loss / Statement", path: "/reports/profitlossstatement/", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
    { name: "3. Payable's", path: "/management/setup/system/getAllSuppliers", roles: ["Admin", "CompanyAdmin","StoreOwner"] },
    { name: "4. Receivable's", path: "/management/setup/system/getAllCustomers", roles: ["Admin", "CompanyAdmin","StoreOwner"] },
    { name: "5. Stock Ledgure", path: "/reports/stockLedgure", roles: ["Admin", "CompanyAdmin","StoreOwner"] },
    { name: "6. Payment Accounts Balances", path: "/reports/payment-accounts-balances", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
    { name: "7. Cash Handover Report", path: "/reports/cash-handover", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
    { name: "8. View All Reports", path: "/reports", roles: ["Admin", "Manager","CompanyAdmin","StoreOwner"] },
  ];

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  // Classic only (null under Full Modern): a quiet 14px text trigger — no underline bar,
  // no pill — that turns dark with a soft grey wash on hover / while its menu is open.
  const chrome = useChromeMenuSx();
  const classicTriggerSx = chrome
    ? {
        '& .MuiListItem-root.MuiListItem-indicators': {
          height: 36,
          px: '10px',
          py: 0,
          borderRadius: '8px',
          color: isOpen ? chrome.k.text : chrome.k.navText,
          bgcolor: isOpen ? chrome.k.hover : 'transparent',
          transition: 'background-color .15s ease, color .15s ease',
          '& .MuiListItemText-root .MuiTypography-root': {
            fontSize: 14,
            fontWeight: 500,
            color: 'inherit',
            '&:before': { display: 'none' }
          },
          '& .MuiSvgIcon-root': { fontSize: 18, color: chrome.k.muted },
          '&:hover, &.active, &:active': { bgcolor: chrome.k.hover, color: chrome.k.text },
          '&.Mui-focusVisible': { outline: `2px solid ${chrome.k.accent}`, outlineOffset: '-2px' }
        }
      }
    : {};

  // Each entry follows the Reports hub's own rule (canSeeReportCard): company owners/admins see all,
  // other users the entries naming their role or whose route permission they hold, and custom
  // company roles granted the Reports hub the management tier — never the owner/admin-only reports.
  // The dropdown is hidden when no entry is visible.
  const { lookup, bypass } = getNavPermissionContext();
  const accessibleForms = forms.filter((form) => canSeeReportCard(form, roles, lookup, bypass));

  return (
    <>
      <ListWrapper
        sx={{
          display: {
            xs: 'none',
            md: 'block'
          },
          ...classicTriggerSx
        }}
      >
        <List disablePadding component={Box} display="flex">
          {accessibleForms.length > 0 && (
            <ListItem
              classes={{ root: 'MuiListItem-indicators' }}
              button
              ref={ref}
              onClick={handleOpen}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary={
                  <Box display="flex" alignItems="center">
                    Reports
                    <Box display="flex" alignItems="center" pl={0.3}>
                      {chrome ? (
                        <KeyboardArrowDownRoundedIcon fontSize="small" />
                      ) : (
                        <ExpandMoreTwoToneIcon fontSize="small" />
                      )}
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          )}
        </List>
      </ListWrapper>

      <Menu
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        PaperProps={chrome ? { sx: { ...chrome.paper, mt: '6px', minWidth: 260 } } : undefined}
        MenuListProps={chrome ? { sx: chrome.list } : undefined}
      >
        {accessibleForms.map((form, index) => (
          <MenuItem
            key={index}
            sx={chrome ? undefined : { px: 3 }}
            component={NavLink}
            to={form.path}
          >
            {form.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default HeaderMenu;
